import { type InertiaLinkProps } from "@inertiajs/react";
import { Loader, type MenuItemProps, Text } from "@mantine/core";

import InstagramIcon from "~icons/basil/instagram-solid";
import PhoneIcon from "~icons/heroicons/device-phone-mobile-20-solid";
import TikTokIcon from "~icons/lineicons/tiktok";

import { useContact } from "~/helpers/contact";
import { type Status } from "~/types";

import classes from "./AppMenu.module.css";

export interface AppMenuProps
  extends BoxProps,
    Omit<ComponentPropsWithoutRef<"div">, "style" | "children" | "onChange"> {}

const AppMenu: FC<AppMenuProps> = ({ ...otherProps }) => {
  const currentUser = useCurrentUser();
  const [contact, { loading: loadingContact }] = useContact();

  // == State
  const [opened, setOpened] = useState(false);

  // == Load server info
  const { data } = useFetchRoute<{ status: Status }>(
    routes.healthcheckHealthchecks.check,
    {
      descriptor: "load server info",
    },
  );
  const { status } = data ?? {};

  // == Logout
  const { submit: logout } = useInertiaForm({
    action: routes.usersSessions.destroy,
    descriptor: "sign out",
  });

  // == Components
  interface MenuLinkProps
    extends MenuItemProps,
      Omit<InertiaLinkProps, "color" | "style"> {}
  const MenuLink: FC<MenuLinkProps> = props => (
    <Menu.Item component={Link} {...props} />
  );

  return (
    <Menu
      position="bottom-end"
      trigger="hover"
      offset={4}
      width={220}
      withinPortal={false}
      {...{ opened }}
      onChange={opened => {
        startTransition(() => {
          setOpened(opened);
        });
      }}
      classNames={{
        item: classes.item,
        itemSection: classes.itemSection,
        itemLabel: classes.itemLabel,
      }}
      {...otherProps}
    >
      <Menu.Target>
        <Badge
          variant="default"
          size="lg"
          leftSection={
            <InfoIcon />
            // currentUser ? (
            //   <Avatar src={currentUser.avatar?.src} size={21} />
            // ) : (
            //   <UserIcon />
            // )
          }
          styles={{
            label: {
              fontWeight: 500,
              maxWidth: 96,
            },
            root: {
              paddingLeft: 8,
            },
          }}
        >
          What is this?
          {/* {currentUser?.name ?? "Sign in & more"} */}
        </Badge>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item component="div" disabled pt={4}>
          <Text size="xs" lh="xs">
            We&apos;re building tools that help you level up your relationship.
          </Text>
        </Menu.Item>
        <MenuLink
          href={routes.patterns.show.path()}
          leftSection={<ContinueIcon />}
          styles={{
            itemLabel: {
              fontWeight: 700,
            },
          }}
        >
          Try our demo
        </MenuLink>
        <MenuLink
          href={routes.bridged.show.path()}
          leftSection={<PhoneIcon />}
          styles={{
            itemLabel: {
              fontWeight: 700,
              lineHeight: 1.2,
            },
          }}
        >
          Try our conflict-resolution tool
        </MenuLink>
        <Menu.Item
          leftSection={loadingContact ? <Loader size={12} /> : <SendIcon />}
          onClick={() => {
            contact();
          }}
        >
          Contact us
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          component="a"
          href="https://www.instagram.com/felt_app/"
          target="_blank"
          rel="noopener noreferrer nofollow"
          leftSection={<InstagramIcon />}
        >
          Instagram
        </Menu.Item>
        <Menu.Item
          component="a"
          href="https://www.tiktok.com/@feltyfeltfelt"
          target="_blank"
          rel="noopener noreferrer nofollow"
          leftSection={<TikTokIcon />}
        >
          TikTok
        </Menu.Item>
        {currentUser && (
          <>
            <Menu.Divider />
            <MenuLink
              href={routes.usersRegistrations.edit.path()}
              leftSection={<AccountIcon />}
            >
              Account
            </MenuLink>
            {currentUser.is_admin && (
              <MenuLink
                href={routes.admin.show.path()}
                leftSection={<AdminIcon />}
              >
                Admin
              </MenuLink>
            )}
            <Menu.Item
              leftSection={<SignOutIcon />}
              onClick={() => {
                logout();
              }}
            >
              Sign out
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item component="div" disabled pt={4}>
              <Text span size="xs">
                Server booted{" "}
                {status ? (
                  <TimeAgo>{status.booted_at}</TimeAgo>
                ) : (
                  <Skeleton
                    display="inline-block"
                    height="min-content"
                    width="fit-content"
                    lh={1}
                    style={{ verticalAlign: "middle" }}
                  >
                    <Text span inherit display="inline-block" lh={1}>
                      2 minutes ago
                    </Text>
                  </Skeleton>
                )}
              </Text>
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default AppMenu;
